import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { HOMEOWNERS_INSURANCE_RATE } from '@/utils/calculator/assumptions';
import { estimateStateClosingTaxes } from '@/utils/fees/stateClosingTaxes';
import { CalculateClosingDisclosureProps, calculateTaxFactor } from '.';
import { calculateEnhancedInterestRate } from './rateIntegration';

// Enhanced guarantee fee calculation based on LTV and down payment percentage
const calculateUSDAGuaranteeFee = (
  downPayment: number,
  purchasePrice: number
) => {
  const downPaymentPercentage = (downPayment / purchasePrice) * 100;
  let usdaGuaranteeFeeRate = 1.25; // 1.25% for 0-2.99% down payment

  // Additional adjustments based on LTV
  if (downPaymentPercentage < 5) {
    usdaGuaranteeFeeRate = 1.25; // 1.25% for 0-2.99% down payment
  } else if (downPaymentPercentage < 10) {
    usdaGuaranteeFeeRate = 1.25; // 1.25% for 0-2.99% down payment
  } else {
    usdaGuaranteeFeeRate = 1.25; // 1.25% for 0-2.99% down payment
  }

  const loanAmount = purchasePrice - downPayment;
  const upfrontGuaranteeFee = (loanAmount * usdaGuaranteeFeeRate) / 100;
  return upfrontGuaranteeFee;
};

export const calculateUSDAClosingDisclosure = (
  {
    offerPrice,
    listPrice,
    downPayment = 0,
    zipCode = 0,
    hoaFees = 0,
    commission = 0,
    marketInterestRate = 6.475,
    creditScore = 0,
    loanTerm = 30,
  }: CalculateClosingDisclosureProps,
  state?: RootState
) => {
  const purchasePrice = offerPrice || listPrice;
  const loanAmount = purchasePrice - downPayment;
  const ltv = loanAmount / purchasePrice;
  const downPaymentPercentage = (downPayment / purchasePrice) * 100;

  const taxFactor = calculateTaxFactor(zipCode);

  // Enhanced guarantee fee calculation
  const upfrontGuaranteeFee = calculateUSDAGuaranteeFee(
    downPayment,
    purchasePrice
  );

  // Enhanced interest rate calculation using the interest rate system
  const interestRate = state
    ? calculateEnhancedInterestRate(
        state,
        'USDA',
        marketInterestRate,
        creditScore,
        ltv,
        loanTerm
      )
    : marketInterestRate; // Fallback to base rate if no state provided

  //change on user input on account page
  const numberOfPayments = loanTerm * 12;
  const adjustedLoanAmount = loanAmount + upfrontGuaranteeFee;

  // constant fees
  const countyRecordingFees = 250;
  const stateRecordingFees = 150;
  const titleTitleSearchBase = 250;
  const recordingDeedBase = 75;
  const appraisalFeeBase = 550;
  const underwritingFee = 1295;
  const applicationFeeBase = 495;
  const recordingTitle = 0; // No separate title recording fee
  const recordingMortgage = 75;
  const surveyFee = 250;
  const pestInspectionFee = 145;
  const floodMonitoringFee = 25;
  const floodDeterminationFee = 35;
  const creditReportFeeBase = 150;
  const processingFeeBase = 795;
  const titleSettlementAgentFee = 650;
  const earnestMoneyDeposit = 1000;
  const insuranceBinder = 150;
  const lenderCredits = 0;
  const initialEscrowMortgageInsurance = 0;
  // Tax proration depends on the closing date; no flat credit is assumed.
  const prepaidsPropertyTaxes = 0;

  // factored calculations
  const annualPropertyTax = (purchasePrice * taxFactor) / 100;
  //UNFACTORED CALCULATIONS, NEED TO FIGURE OUT HOW TO INCLUDE CREDIT AND STATE FACTORS
  const optionalOwnersTitleInsuranceBase = purchasePrice * 0.00575; //State, Credit
  const minimumOwnersTitle = 650;
  const maximumOwnersTitle = 3850;
  const optionalOwnersTitleInsurance = Math.min(
    Math.max(optionalOwnersTitleInsuranceBase, minimumOwnersTitle),
    maximumOwnersTitle
  );
  const recordingTransferTaxBase = purchasePrice * 0.01; //State dependent.

  // Dynamic monthly guarantee fee based on adjusted loan amount
  const monthlyGuaranteeFee = (adjustedLoanAmount * 0.0035) / 12;
  const monthlyHomeownersInsurance =
    (purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12;
  const monthlyPropertyTaxes = annualPropertyTax / 12;
  const prepaidsHomeownersInsurance =
    ((purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12) * 3;
  const upfrontHomeownersInsurance = purchasePrice * HOMEOWNERS_INSURANCE_RATE;
  // Lender's policy covers the loan amount.
  const titleLendersTitleInsuranceBase = loanAmount * 0.004;
  const homeInspectionFeeBase = purchasePrice * 0.0035;
  const minimumInspectionFee = 650;
  const maximumInspectionFee = 4500;
  const homeInspectionFee = Math.min(
    Math.max(homeInspectionFeeBase, minimumInspectionFee),
    maximumInspectionFee
  );
  const initialEscrowTaxes = monthlyPropertyTaxes * 6; //
  const initialEscrowHOI = ((purchasePrice * 0.01) / 12) * 3;
  const estimatedEscrow =
    monthlyHomeownersInsurance + monthlyGuaranteeFee + monthlyPropertyTaxes;

  //these aren't going to change, they are determined
  const dailyInterestRate = interestRate / 365 / 100; // Convert annual rate to daily decimal
  const prepaidInterest = adjustedLoanAmount * dailyInterestRate * 15; // Return the result rounded to 2 decimal places
  const monthlyInterestRate = interestRate / 12 / 100; // Convert annual rate to monthly decimal
  const principalAndInterest =
    (adjustedLoanAmount *
      (monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  const piti = principalAndInterest + estimatedEscrow + monthlyGuaranteeFee;
  const transferTaxes = 0; // No transfer tax on refinance
  // Borrower's loan taxes still apply on a refinance (e.g. Florida doc
  // stamps + intangible tax) — see utils/fees/stateClosingTaxes.
  const stateTaxes = estimateStateClosingTaxes({
    zipCode,
    price: purchasePrice,
    loanAmount,
    transaction: 'refinance',
  });
  const mortgageTaxes = stateTaxes.mortgageTaxes;
  const cleanPayment = piti;
  // No owner's title policy on a refinance — the owner bought one at purchase.
  const titleCosts =
    insuranceBinder +
    titleTitleSearchBase +
    titleLendersTitleInsuranceBase +
    surveyFee +
    titleSettlementAgentFee;
  const prepaidCosts =
    initialEscrowTaxes +
    prepaidsPropertyTaxes +
    prepaidInterest +
    prepaidsHomeownersInsurance;
  const govCosts =
    stateRecordingFees +
    countyRecordingFees +
    recordingMortgage +
    recordingTitle +
    recordingDeedBase +
    transferTaxes +
    mortgageTaxes;
  const lenderCostTotals =
    floodDeterminationFee +
    floodMonitoringFee +
    creditReportFeeBase +
    appraisalFeeBase +
    applicationFeeBase +
    underwritingFee +
    processingFeeBase;
  const closingCosts = govCosts + titleCosts + lenderCostTotals + prepaidCosts;
  const cashOutsideClosing =
    appraisalFeeBase + earnestMoneyDeposit + homeInspectionFee;
  const cashToClose = closingCosts + downPayment - earnestMoneyDeposit;
  const unbeatableCredit = (purchasePrice * commission) / 100; //this is where we put the buyers agent commission
  const unbeatableDeal = cashToClose - unbeatableCredit;

  // Perform calculations using the provided purchase price and constants
  // Prepare the closing disclosure object
  const closingDisclosure = {
    type: USER_LOAN_PROGRAM.USDA,
    offerPrice,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate,
    creditScore,
    downPayment,
    loanAmount: adjustedLoanAmount,
    interestRate,
    ltv,
    cleanPayment,
    monthlyGuaranteeFee,
    processingFeeBase,
    principalAndInterest,
    homeInspectionFee,
    loanTerm,
    optionalOwnersTitleInsurance,
    hoaFees,
    earnestMoneyDeposit,
    piti,
    estimatedEscrow,
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    prepaidsHomeownersInsurance,
    prepaidsPropertyTaxes,
    upfrontGuaranteeFee,
    prepaidInterest,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    titleTitleSearchBase,
    insuranceBinder,
    recordingDeedBase,
    appraisalFeeBase,
    underwritingFee,
    applicationFeeBase,
    pestInspectionFee,
    floodMonitoringFee,
    floodDeterminationFee,
    creditReportFeeBase,
    recordingTitle,
    recordingMortgage,
    countyRecordingFees,
    stateRecordingFees,
    upfrontHomeownersInsurance,
    cashOutsideClosing,
    surveyFee,
    initialEscrowTaxes,
    initialEscrowMortgageInsurance,
    initialEscrowHOI,
    lenderCredits,
    lenderCostTotals,
    titleCosts,
    prepaidCosts,
    govCosts,
    closingCosts,
    // Exactly what closingCosts sums — the fee breakdown itemizes these.
    closingCostParts: {
      stateRecordingFees,
      countyRecordingFees,
      recordingMortgage,
      recordingTitle,
      recordingDeedBase,
      transferTaxes,
      mortgageTaxes,
      insuranceBinder,
      titleTitleSearchBase,
      titleLendersTitleInsuranceBase,
      surveyFee,
      titleSettlementAgentFee,
      floodDeterminationFee,
      floodMonitoringFee,
      creditReportFeeBase,
      appraisalFeeBase,
      applicationFeeBase,
      underwritingFee,
      processingFeeBase,
      initialEscrowTaxes,
      prepaidsPropertyTaxes,
      prepaidInterest,
      prepaidsHomeownersInsurance,
    },
    cashToClose,
    unbeatableDeal,
    transferTaxes,
    mortgageTaxes,
    stateTaxLines: stateTaxes.lines,
    stateTaxState: stateTaxes.state,
  };
  return closingDisclosure;
};

export type ProductUSDAEsitmation = ReturnType<
  typeof calculateUSDAClosingDisclosure
>;
